import { useState } from "react";
import { Calendar, Upload, X, FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { attendanceAPI } from "@/lib/api";

export default function AbsenceRequestForm({
  isOpen,
  onClose,
  onSuccess,
  absenceTypes,
  userRole,
}) {
  const [formData, setFormData] = useState({
    typeId: "",
    startDate: "",
    endDate: "",
    reason: "",
    justificationFile: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError("");
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];
    if (!allowedTypes.includes(file.type)) {
      setError("Type de fichier non autorisé. Utilisez PDF, JPG ou PNG.");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError("Le fichier ne doit pas dépasser 5MB.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      justificationFile: file,
    }));
    setError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeFile = () => {
    setFormData((prev) => ({
      ...prev,
      justificationFile: null,
    }));
  };

  const validateForm = () => {
    console.log("Validating form with data:", formData);

    if (!formData.typeId) {
      setError("Veuillez sélectionner un type d'absence");
      console.log("Validation failed: no type selected");
      return false;
    }
    if (!formData.startDate) {
      setError("Veuillez sélectionner une date de début");
      console.log("Validation failed: no start date");
      return false;
    }
    if (!formData.endDate) {
      setError("Veuillez sélectionner une date de fin");
      console.log("Validation failed: no end date");
      return false;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setError(
        "La date de fin doit être égale ou postérieure à la date de début"
      );
      console.log("Validation failed: end date before start date");
      return false;
    }
    // Note: reason (motif) is optional in the schema, so we don't validate it
    console.log("Validation passed");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted", formData);

    if (!validateForm()) {
      console.log("Form validation failed");
      return;
    }

    console.log("Form validation passed, submitting...");
    setLoading(true);
    setError("");

    try {
      const submitData = new FormData();
      submitData.append("typeId", formData.typeId);
      submitData.append("startDate", formData.startDate);
      submitData.append("endDate", formData.endDate);
      // Only append reason if it's not empty
      if (formData.reason && formData.reason.trim()) {
        submitData.append("reason", formData.reason.trim());
      }

      if (formData.justificationFile) {
        submitData.append("justificationFile", formData.justificationFile);
      }

      console.log("Sending request to API...");
      await attendanceAPI.createAbsenceRequest(submitData);

      if (onSuccess) onSuccess();
      onClose(); // Close the modal on success
    } catch (err) {
      console.error("Error creating absence request:", err);
      setError(
        err.response?.data?.message ||
          "Erreur lors de la création de la demande d'absence."
      );
    } finally {
      setLoading(false);
    }
  };

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calendar className="h-6 w-6 text-blue-600" />
            Nouvelle demande d'absence
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Type d'absence */}
          <div className="space-y-2">
            <Label htmlFor="typeId">Type d'absence *</Label>
            <Select
              value={formData.typeId}
              onValueChange={(value) => handleInputChange("typeId", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un type d'absence" />
              </SelectTrigger>
              <SelectContent>
                {absenceTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id.toString()}>
                    {type.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Date de début *</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange("startDate", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">Date de fin *</Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange("endDate", e.target.value)}
                min={
                  formData.startDate || new Date().toISOString().split("T")[0]
                }
              />
            </div>
          </div>

          {/* Duration indicator */}
          {formData.startDate && formData.endDate && (
            <div className="text-center">
              <Badge variant="outline" className="text-sm">
                Durée: {calculateDays()} jour{calculateDays() > 1 ? "s" : ""}
              </Badge>
            </div>
          )}

          {/* Raison */}
          <div className="space-y-2">
            <Label htmlFor="reason">Raison de l'absence (optionnel)</Label>
            <Textarea
              id="reason"
              placeholder="Expliquez la raison de votre absence (optionnel)..."
              value={formData.reason}
              onChange={(e) => handleInputChange("reason", e.target.value)}
              rows={4}
              className="min-h-[100px]"
            />
            <p className="text-xs text-gray-500">
              Ce champ est optionnel. Vous pouvez laisser vide si vous n'avez
              pas de raison particulière à mentionner.
            </p>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Document justificatif (optionnel)</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragOver
                  ? "border-primary bg-primary/5"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {formData.justificationFile ? (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-gray-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium">
                        {formData.justificationFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(
                          formData.justificationFile.size /
                          1024 /
                          1024
                        ).toFixed(2)}{" "}
                        MB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeFile}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-1">
                    Glissez un fichier ici ou cliquez pour sélectionner
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF, JPG, PNG (max 5MB)
                  </p>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    id="fileInput"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => document.getElementById("fileInput").click()}
                  >
                    Sélectionner un fichier
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? "Création en cours..." : "Créer la demande"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
